/**
 * Get YouTube thumbnail URLs with fallback options
 * @param {string} videoId - YouTube video ID
 * @returns {object} Object containing thumbnail URLs in order of quality
 */
export function getThumbnailUrls(videoId) {
  return {
    maxresdefault: `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`,
    sddefault: `https://img.youtube.com/vi/${videoId}/sddefault.jpg`,
    hqdefault: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
    mqdefault: `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`,
    default: `https://img.youtube.com/vi/${videoId}/default.jpg`,
  };
}

/**
 * Get the best quality thumbnail URL for a video
 * @param {string} videoId - YouTube video ID
 * @returns {string} Highest quality thumbnail URL
 */
export function getBestThumbnailUrl(videoId) {
  return getThumbnailUrls(videoId).maxresdefault;
}

/**
 * Get the URL of a deleted video's thumbnail recovered from the Wayback Machine
 * (served by our backend from Oracle Cloud; 404 when none was recovered)
 * @param {string} videoId - YouTube video ID
 * @returns {string} Recovered thumbnail URL
 */
export function getRecoveredThumbnailUrl(videoId) {
  return `/api/thumbnails/recovered/${videoId}`;
}

/**
 * Get thumbnail URL with fallback chain
 * Returns URLs in order of quality preference
 * @param {string} videoId - YouTube video ID
 * @returns {string[]} Array of thumbnail URLs in order of quality (best to worst)
 */
export function getThumbnailFallbackChain(videoId) {
  const urls = getThumbnailUrls(videoId);
  // hqdefault exists for every live video, so the recovered thumbnail is only
  // reached once YouTube has dropped the video's thumbnails, i.e. it was deleted.
  // It goes before mqdefault because YouTube's CDN can keep a stale mqdefault
  // of a deleted video around.
  return [
    urls.sddefault,
    urls.hqdefault,
    getRecoveredThumbnailUrl(videoId),
    urls.mqdefault,
    urls.default,
  ];
}

/**
 * Generate YouTube watch URL
 * @param {string} videoId - YouTube video ID
 * @returns {string} YouTube watch URL
 */
export function getYouTubeWatchUrl(videoId) {
  return `https://www.youtube.com/watch?v=${videoId}`;
}

/**
 * Format subscriber count in YouTube style (e.g., 1.1k, 1.5M)
 * @param {number} count - Subscriber count
 * @returns {string} Formatted subscriber count string
 */
export function formatSubscriberCount(count) {
  if (!count || count === 0) return '';
  
  if (count >= 1000000) {
    const millions = count / 1000000;
    // Round to 1 decimal place, but don't show .0
    if (millions >= 10) {
      return `${Math.floor(millions)}M`;
    }
    return `${Math.round(millions * 10) / 10}M`;
  }
  
  if (count >= 1000) {
    const thousands = count / 1000;
    // Round to 1 decimal place, but don't show .0
    if (thousands >= 10) {
      return `${Math.floor(thousands)}k`;
    }
    return `${Math.round(thousands * 10) / 10}k`;
  }
  
  return count.toLocaleString();
}
